import {
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  VisuallyHiddenInput,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  useGetRespondentStatusQuery,
  useUpdateRespondentMutation,
} from '../../../app/services/api'
import { RespondentData, RespondentGroup } from './RespondentInfo'

const UpdateRespondentModal: FC<any> = ({
  existingRespondent,
  isOpen,
  onClose,
  updateRespondent,
  isLoading,
}) => {
  const { t } = useTranslation()
  const form = useForm({
    defaultValues: {
      newEmail: existingRespondent?.email,
      previousEmail: existingRespondent?.email,
      type: existingRespondent?.type,
    },
  })

  useEffect(() => {
    form.reset({
      newEmail: existingRespondent?.email,
      previousEmail: existingRespondent?.email,
      type: existingRespondent?.type,
    })
  }, [existingRespondent, form])

  return (
    <Modal isOpen={isOpen} onClose={onClose} onEsc={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton onClick={onClose} />
        <ModalHeader>
          {existingRespondent?.email
            ? t('update_respondent')
            : t('add_respondent')}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={form.handleSubmit(updateRespondent)}>
            <FormControl isInvalid={!!form.formState.errors.newEmail} mb={3}>
              <FormLabel>{t('email')}</FormLabel>
              <Input
                type="email"
                {...form.register('newEmail', { required: true })}
              />
              <FormErrorMessage>{t('required_field')}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!form.formState.errors.type} mb={3}>
              <FormLabel>{t('type')}</FormLabel>
              <Select {...form.register('type', { required: true })}>
                <option
                  key={'manager'}
                  value={'manager'}
                  selected={existingRespondent?.type === 'manager'}
                >
                  {t('manager')}
                </option>
                <option
                  key={'peer'}
                  value={'peer'}
                  selected={existingRespondent?.type === 'peer'}
                >
                  {t('peer')}
                </option>
                <option
                  key={'reportee'}
                  value={'reportee'}
                  selected={existingRespondent?.type === 'reportee'}
                >
                  {t('reportee')}
                </option>
              </Select>
              <FormErrorMessage>{t('required_field')}</FormErrorMessage>
            </FormControl>
            <VisuallyHiddenInput {...form.register('previousEmail')} />
            <Button
              isLoading={isLoading}
              width="100%"
              my={3}
              colorScheme="primary"
              type="submit"
            >
              {t('submit')}
            </Button>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

const EndOfJourneyPeerManagement: React.FC<any> = () => {
  const { t } = useTranslation()
  const { data: respondentData, isLoading } = useGetRespondentStatusQuery({
    type: 'peer-end-of-journey',
  })

  const [existingRespondent, setExistingRespondent] = useState()
  const [updateRespondent] = useUpdateRespondentMutation()
  const {
    isOpen: isRespondentModalOpen,
    onOpen: onRespondentOpen,
    onClose: onRespondentClose,
  } = useDisclosure()

  const toast = useToast()

  const onCloseRespondentModal = useCallback(() => {
    setExistingRespondent(undefined)
    onRespondentClose()
  }, [onRespondentClose])

  const onUpdateRespondent = useCallback(
    async (data: any) => {
      try {
        await updateRespondent({
          previousEmail: data.previousEmail ? data.previousEmail : undefined,
          newEmail: data.newEmail,
          type: data.type,
        }).unwrap()

        toast({
          status: 'success',
          title: t('success'),
          description: t('respondent_updated'),
          isClosable: true,
        })
      } catch (err: any) {
        console.log('error updating respondent', err)
        toast({
          status: 'error',
          title: 'Error',
          description: err?.data?.message ?? t('respondent_update_failed'),
          isClosable: true,
        })
      }
      onCloseRespondentModal()
    },
    [updateRespondent, onCloseRespondentModal, toast]
  )
  // const onAddRespondentOpen = useCallback(() => {
  //   const respondent: any = {
  //     email: '',
  //     type: 'peer',
  //   }
  //   setExistingRespondent(respondent)
  //   onRespondentOpen()
  // }, [onRespondentOpen])

  if (isLoading) {
    return (
      <Center px={4} py={2} width={'100%'}>
        <Spinner />
      </Center>
    )
  }
  if (!respondentData) {
    return (
      <Center px={4} py={2}>
        <Text>{t('no_data')}</Text>
      </Center>
    )
  }
  const onEditRespondent = (respondent: any) => {
    setExistingRespondent(respondent)
    onRespondentOpen()
  }

  return (
    <Box px={4} py={2} pt={8}>
      {/* <Box justifyContent={'flex-end'} display="flex">
        <Button colorScheme="primary" onClick={onAddRespondentOpen} ml={4}>
          {t('add_respondent')}
        </Button>
      </Box> */}
      <RespondentGroup
        type="manager"
        respondents={respondentData.managers as RespondentData[]}
        labelSingular={t('manager')}
        labelPlural={t('managers')}
        onEditRespondent={onEditRespondent}
        nonEditable={true}
      />
      <RespondentGroup
        type="peer"
        respondents={respondentData.peers as RespondentData[]}
        labelSingular={t('peer')}
        labelPlural={t('peers')}
        onEditRespondent={onEditRespondent}
        nonEditable={true}
      />
      <RespondentGroup
        type="reportee"
        respondents={respondentData.reportees as RespondentData[]}
        labelSingular={t('reportee')}
        labelPlural={t('reportees')}
        onEditRespondent={onEditRespondent}
        nonEditable={true}
      />
      <UpdateRespondentModal
        existingRespondent={existingRespondent}
        isOpen={isRespondentModalOpen}
        onClose={onCloseRespondentModal}
        updateRespondent={onUpdateRespondent}
        isLoading={isLoading}
      />
    </Box>
  )
}

export default EndOfJourneyPeerManagement
